import IcEdit from '@assets/details-camera-icons/IcEdit';
import ICDelete from '@assets/jsx-icon/ICDelete';
import { BaseHead, Sort } from '@components/tables';
import { tableHeadStyles } from '@components/tables/BaseTable';
import { TableHeadCellSort } from '@components/tables/TableCellSort';
import TableSkeleton from '@components/tables/TableSkeleton';
import { pushError, pushSuccess } from '@components/toast';
import { taskHttpController } from '@controllers/index';
import { ListProps, Paging } from '@Core';
import { CameraTask, TaskPriority, TaskStatus } from '@EcamModel/model/CameraTask';
import useMedia from '@hooks/useMedia';
import { UsePopUpReturnType } from '@hooks/usePopUp';
import {
    Badge,
    Box,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { BaseSelect, Filter } from '@pages/cameras/list';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import SearchTypeSelect from '@pages/cameras/list/components/SearchTypeSelect';
import { DangerTooltip } from '@pages/cameras/list/components/TooltipDangerIconButton';
import { BaseItem } from '@pages/cameras/list/styles';
import { CellOfRow } from '@pages/overview/components/HeadOverview';
import { OverdueButton } from '@pages/overview/components/PopUpTaskInUnCompleteCamera';
import color from '@theme/Colors';
import _ from 'lodash';
import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { IoEyeOutline } from 'react-icons/io5';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { formatUKDate } from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { ellipsisStyles, scrollbarStyles } from '..';
import {
    AsTypeSort,
    CameraSubTasks,
    columnsHeadCameraTask,
    DropdownType,
    mappingPriorityCamera,
    mappingPriorityZone,
    OwnFilterCameraTask,
    searchTypes,
    stickiesStyle,
    SubTaskStatus,
} from '../config';
import EditableTaskNameCell from './EditableCellProps';
import StatusPriorityDropdown from './StatusPriorityDropdown';
import SubTaskTooltipContent from './SubTaskTooltipContent';

type Props = {
    isShowTaskManagement?: boolean;
    isLoading: boolean;
    listTask: Paging<CameraTask>;
    filter: Partial<Filter>;
    taskItem: CameraTask;
    popupViewCompleteTask: UsePopUpReturnType;
    popUpCountAttachments: UsePopUpReturnType;
    popupUpsertTask: UsePopUpReturnType;
    popupConfirmDeleteTask: UsePopUpReturnType;
    filterSortState: ListProps<CameraTask>;
    onSetTaskItem: (task: CameraTask) => void;
    onSetFilter: Dispatch<SetStateAction<Partial<Filter>>>;
    onSetPartialFilter: (filter: Partial<Filter>) => void;
    onSetFilterSortState: Dispatch<SetStateAction<OwnFilterCameraTask>>;
    handleChangeStatusOrPriority: (
        selectedItem: TaskStatus | TaskPriority | null,
        taskId: number,
        type: DropdownType
    ) => void;
};
export default function ListDataTask(props: Props) {
    const { isMobileSM } = useMedia();
    const queryClient = useQueryClient();
    const backdrop = useBackdrop();

    const {
        isShowTaskManagement,
        isLoading,
        listTask,
        filter,
        taskItem,
        popupViewCompleteTask,
        popUpCountAttachments,
        popupUpsertTask,
        popupConfirmDeleteTask,
        filterSortState,
        onSetFilterSortState,
        onSetTaskItem,
        onSetFilter,
        onSetPartialFilter,
        handleChangeStatusOrPriority,
    } = props;

    const mutationUpdateSubTask = useMutation(
        async ({ task, updatedSubTasks }: { task: CameraTask; updatedSubTasks: CameraSubTasks[] }) => {
            backdrop.setTrue();
            const response = await taskHttpController.upsert({
                Id: task.Id,
                SubTask: JSON.stringify(updatedSubTasks),
            } as CameraTask);
            return response;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                queryClient.invalidateQueries(['listTask']).finally(() => {
                    pushSuccess('Sub-task updated successfully');
                    backdrop.setFalse();
                });
            },
            onError: (error: any) => {
                backdrop.setFalse();
                pushError(error?.message || 'Failed to update sub-task');
            },
            onSettled: () => {},
        }
    );

    const handleToggleSubTask = async (task: CameraTask, subTaskIndex: number, currentStatus: SubTaskStatus) => {
        try {
            const parsedSubTasks: CameraSubTasks[] = JSON.parse(task.SubTask ?? '[]');
            const updatedSubTasks = [...parsedSubTasks];

            updatedSubTasks[subTaskIndex] = {
                ...updatedSubTasks[subTaskIndex],
                Status: currentStatus === SubTaskStatus.Done ? SubTaskStatus.Todo : SubTaskStatus.Done,
                CompletedAt: currentStatus === SubTaskStatus.Done ? null : new Date(),
            };

            if (task.TaskStatus === TaskStatus.Completed) {
                pushError('Cannot update sub-tasks of a completed task');
                return;
            }

            await mutationUpdateSubTask.mutateAsync({ task, updatedSubTasks });
        } catch (error) {
            console.error('Error updating sub-task:', error);
        }
    };

    const handleSortChange = (sort: Sort | undefined, sortKey: keyof CameraTask) => {
        onSetFilterSortState((prevState) => {
            let newSorts = [...(prevState.sorts || [])];

            const defaultIndex = newSorts.indexOf('-Created');
            if (defaultIndex !== -1) {
                newSorts.splice(defaultIndex, 1);
            }

            const isDescending = sort === 'DSC';
            const formattedSortKey = isDescending ? (`-${String(sortKey)}` as keyof AsTypeSort<CameraTask>) : sortKey;

            const existingIndex = newSorts.findIndex(
                (s) => s === sortKey || s === (`-${String(sortKey)}` as keyof AsTypeSort<CameraTask>)
            );

            if (sort === undefined) {
                if (existingIndex !== -1) {
                    newSorts.splice(existingIndex, 1);
                }
            } else {
                if (existingIndex !== -1) {
                    newSorts[existingIndex] = formattedSortKey;
                } else {
                    newSorts.push(formattedSortKey);
                }
            }

            return {
                ...prevState,
                sorts: newSorts,
            };
        });
    };

    return (
        <BaseItem>
            <Stack width={'100%'}>
                <TextField
                    sx={{ '& .MuiOutlinedInput-root': { paddingLeft: 0 } }}
                    fullWidth
                    label="Search by"
                    placeholder={`Search by ${filter.searchType?.Name.toLowerCase()}`}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        onSetPartialFilter({ search: value });
                    }}
                    value={filter.search}
                    InputProps={{
                        startAdornment: (
                            <Stack direction="row">
                                <SearchTypeSelect
                                    label="Search by"
                                    minWidth={80}
                                    listData={searchTypes}
                                    value={filter.searchType!}
                                    keyEqual="Id"
                                    keyLabel="Name"
                                    handleChange={(e) => {
                                        onSetFilter((prev) => ({
                                            ...prev,
                                            searchType: e,
                                            page: filter.search ? 1 : filter.page,
                                        }));
                                    }}
                                />
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                        mx: '5px',
                                        borderRightWidth: 1,
                                        borderColor: color.grey600,
                                    }}
                                    variant="middle"
                                />
                            </Stack>
                        ),
                        endAdornment: (
                            <FiSearch
                                color="disabled"
                                size={22}
                                style={{ color: 'rgba(0, 0, 0, 0.38)', cursor: 'pointer' }}
                            />
                        ),
                    }}
                />
            </Stack>

            <Box mt={2}>
                {isLoading ? (
                    <TableSkeleton itemsColumnLength={isMobileSM ? 3 : 10} />
                ) : listTask?.rows && listTask?.rows?.length > 0 ? (
                    <TableContainer
                        component={Box}
                        sx={{
                            '& .chart-scroll-bar': scrollbarStyles,
                        }}
                    >
                        <ScrollContainer
                            className="container chart-scroll-bar"
                            style={{
                                height: '100%',
                                width: '100%',
                                paddingBottom: '5px',
                                position: 'relative',
                                maxHeight: 700,
                                overflowX: 'auto',
                            }}
                            hideScrollbars={false}
                        >
                            <Table aria-label="simple table">
                                <TableHead
                                    sx={{
                                        ...tableHeadStyles({
                                            style: {
                                                height: 0,
                                            },
                                        }),
                                    }}
                                >
                                    <TableRow>
                                        {columnsHeadCameraTask.map((column) => {
                                            if (
                                                (column.sortKey === 'CameraName' ||
                                                    column.sortKey === 'CameraPriority') &&
                                                isShowTaskManagement
                                            ) {
                                                return null;
                                            }

                                            const sort =
                                                column.sortKey &&
                                                (filterSortState.sorts?.includes(`-${column.sortKey}`)
                                                    ? 'DSC'
                                                    : filterSortState.sorts?.includes(column.sortKey)
                                                    ? 'ASC'
                                                    : undefined);

                                            const findIndexOrderBy = filterSortState.sorts?.findIndex(
                                                (item) => (item as string).replace('-', '') === column.sortKey
                                            );

                                            return column.sortKey ? (
                                                <TableHeadCellSort
                                                    key={column.sortKey}
                                                    align={column.align}
                                                    title={column.title}
                                                    isLeftLabel={column.isLeftLabel}
                                                    sx={
                                                        column.sortKey === 'TaskName'
                                                            ? isShowTaskManagement
                                                                ? stickiesStyle({
                                                                      style: {
                                                                          minWidth: 'max-content',
                                                                          left: '57px',
                                                                      },
                                                                  })
                                                                : { minWidth: 'max-content' }
                                                            : { minWidth: 'max-content', ...column.sx }
                                                    }
                                                    sortKey={column.sortKey}
                                                    sort={sort}
                                                    sortedCount={
                                                        _.isNil(findIndexOrderBy) ? undefined : findIndexOrderBy + 1
                                                    }
                                                    onChangeSort={handleSortChange}
                                                />
                                            ) : (
                                                <BaseHead
                                                    key={column.title as string}
                                                    align={column.align}
                                                    title={column.title}
                                                    sx={{
                                                        minWidth: 'max-content',
                                                        ...column.sx,
                                                    }}
                                                />
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {listTask.rows?.map((task, index) => {
                                        const serialNumber = (filter.page! - 1) * 10 + index + 1;
                                        const parsedTaskTags: CameraSubTasks[] = JSON.parse(task.SubTask ?? '[]');
                                        const formattedTaskTags = parsedTaskTags?.map(
                                            (task, index, array) =>
                                                `${task.Name} (${task.Status})${index === array.length - 1 ? '.' : ','}`
                                        );

                                        const displayTaskTags = formattedTaskTags.slice(0, 2).join(', ');
                                        const remainingTaskTagsCount = formattedTaskTags.length - 2;
                                        const isMoreThanTwoTaskTags = remainingTaskTagsCount > 0;

                                        let parsedTaskTypes: BaseSelect[] = [];

                                        try {
                                            const taskTypeString = task?.TaskType ?? '';
                                            if (taskTypeString) {
                                                parsedTaskTypes = JSON.parse(taskTypeString);
                                            }
                                        } catch (error) {
                                            console.error('Error parsing TaskType JSON:', error);
                                            parsedTaskTypes = [];
                                        }

                                        const taskTypeString = parsedTaskTypes?.map((taskType) => taskType);
                                        const displayTaskTypes = taskTypeString?.slice(0, 2).join(', ');
                                        const remainingTaskTypesCount = taskTypeString.length - 2;
                                        const isMoreThanTwoTaskTypes = remainingTaskTypesCount > 0;

                                        return (
                                            <TableRow key={index}>
                                                {/* Serial number */}
                                                <CellOfRow
                                                    value={<Typography component={'span'}>{serialNumber}</Typography>}
                                                    align="left"
                                                    sx={stickiesStyle({ style: { left: 0 } })}
                                                />
                                                {/* Camera name - Camera priority*/}
                                                {!isShowTaskManagement && (
                                                    <>
                                                        <CellOfRow
                                                            value={
                                                                <Link
                                                                    to={`/cameras/${task.Camera?.Id}`}
                                                                    style={{ textDecoration: 'none' }}
                                                                    target="_blank"
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            ...ellipsisStyles,
                                                                            color: color.secondary,
                                                                            cursor: 'pointer',
                                                                            minWidth:
                                                                                task.Camera?.Name?.length! > 50
                                                                                    ? '350px'
                                                                                    : 'max-content',
                                                                        }}
                                                                    >
                                                                        {task.Camera?.Name}
                                                                    </Typography>
                                                                </Link>
                                                            }
                                                            align="left"
                                                            sx={stickiesStyle({ style: { left: '57px' } })}
                                                        />

                                                        <CellOfRow
                                                            value={
                                                                <Typography
                                                                    component={'span'}
                                                                    sx={{
                                                                        ...ellipsisStyles,
                                                                        cursor: 'default',
                                                                        minWidth: '120px',
                                                                    }}
                                                                >
                                                                    {mappingPriorityCamera[task.CameraPriority!] ??
                                                                        'N/A'}
                                                                </Typography>
                                                            }
                                                            align="center"
                                                        />
                                                    </>
                                                )}
                                                {/* Task name */}
                                                <EditableTaskNameCell
                                                    taskId={task.Id!}
                                                    taskName={task.TaskName}
                                                    taskItem={taskItem}
                                                    isShowTaskManagement={isShowTaskManagement}
                                                    style={
                                                        isShowTaskManagement
                                                            ? stickiesStyle({ style: { left: '57px' } })
                                                            : {}
                                                    }
                                                />
                                                {/* Task types */}
                                                <CellOfRow
                                                    value={
                                                        <>
                                                            {task.SubTask ? (
                                                                <>
                                                                    {isMoreThanTwoTaskTags ? (
                                                                        <Tooltip
                                                                            title={
                                                                                <SubTaskTooltipContent
                                                                                    subTasks={parsedTaskTags}
                                                                                    onToggleSubTask={(
                                                                                        index,
                                                                                        currentStatus
                                                                                    ) =>
                                                                                        handleToggleSubTask(
                                                                                            task,
                                                                                            index,
                                                                                            currentStatus
                                                                                        )
                                                                                    }
                                                                                />
                                                                            }
                                                                            placement="top"
                                                                            sx={{
                                                                                cursor: 'default',
                                                                            }}
                                                                            PopperProps={{
                                                                                sx: {
                                                                                    '.MuiTooltip-tooltip': {
                                                                                        backgroundColor: color.white,
                                                                                        boxShadow:
                                                                                            'rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px',
                                                                                    },
                                                                                },
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    ...ellipsisStyles,
                                                                                    cursor: 'default',
                                                                                    minWidth:
                                                                                        displayTaskTags?.length! <= 10
                                                                                            ? 'max-content'
                                                                                            : !displayTaskTags
                                                                                            ? '120px'
                                                                                            : '280px',
                                                                                    whiteSpace: 'pre-line',
                                                                                    color: color.secondary,
                                                                                }}
                                                                                component={'span'}
                                                                            >
                                                                                {displayTaskTags.replace(/, /g, '\n')}{' '}
                                                                                ...+
                                                                                {remainingTaskTagsCount}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    ) : (
                                                                        <Tooltip
                                                                            title={
                                                                                <SubTaskTooltipContent
                                                                                    subTasks={parsedTaskTags}
                                                                                    onToggleSubTask={(
                                                                                        index,
                                                                                        currentStatus
                                                                                    ) =>
                                                                                        handleToggleSubTask(
                                                                                            task,
                                                                                            index,
                                                                                            currentStatus
                                                                                        )
                                                                                    }
                                                                                />
                                                                            }
                                                                            PopperProps={{
                                                                                sx: {
                                                                                    '.MuiTooltip-tooltip': {
                                                                                        backgroundColor: color.white,
                                                                                        boxShadow:
                                                                                            'rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px',
                                                                                    },
                                                                                },
                                                                            }}
                                                                            placement="top"
                                                                            sx={{ cursor: 'default' }}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    ...ellipsisStyles,
                                                                                    minWidth:
                                                                                        displayTaskTags?.length! >= 10
                                                                                            ? '280px'
                                                                                            : 'max-content',
                                                                                    whiteSpace: 'pre-line',
                                                                                    color: color.secondary,
                                                                                    cursor: 'default',
                                                                                }}
                                                                                component={'span'}
                                                                            >
                                                                                {displayTaskTags.replace(/, /g, '\n')}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    )}
                                                                </>
                                                            ) : task.TaskType ? (
                                                                <>
                                                                    {isMoreThanTwoTaskTypes ? (
                                                                        <Tooltip
                                                                            title={taskTypeString.join(', ')}
                                                                            placement="top"
                                                                            sx={{ cursor: 'default' }}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    ...ellipsisStyles,
                                                                                    cursor: 'default',
                                                                                    minWidth:
                                                                                        displayTaskTypes?.length! <= 10
                                                                                            ? 'max-content'
                                                                                            : !displayTaskTypes
                                                                                            ? '120px'
                                                                                            : '180px',
                                                                                }}
                                                                                component={'span'}
                                                                            >
                                                                                {displayTaskTypes}, ...+
                                                                                {remainingTaskTagsCount}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    ) : (
                                                                        <Typography
                                                                            sx={{
                                                                                ...ellipsisStyles,
                                                                                minWidth:
                                                                                    displayTaskTypes?.length! >= 10
                                                                                        ? '180px'
                                                                                        : 'max-content',
                                                                            }}
                                                                            component={'span'}
                                                                        >
                                                                            {displayTaskTypes}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <></>
                                                            )}
                                                        </>
                                                    }
                                                    align="left"
                                                />
                                                {/* Location name */}
                                                <CellOfRow
                                                    value={
                                                        <Typography
                                                            component={'span'}
                                                            sx={{
                                                                ...ellipsisStyles,
                                                                cursor: 'default',
                                                                minWidth: 'max-content',
                                                            }}
                                                        >
                                                            {task.LocationName}
                                                        </Typography>
                                                    }
                                                    align="left"
                                                />
                                                {/* Zone name */}
                                                <CellOfRow
                                                    value={
                                                        <Typography
                                                            component={'span'}
                                                            sx={{
                                                                ...ellipsisStyles,
                                                                cursor: 'default',
                                                                minWidth: 'max-content',
                                                            }}
                                                        >
                                                            {task.ZoneName}
                                                        </Typography>
                                                    }
                                                    align="left"
                                                />
                                                {/* Zone priority */}
                                                <CellOfRow
                                                    value={
                                                        <Typography
                                                            component={'span'}
                                                            sx={{
                                                                ...ellipsisStyles,
                                                                cursor: 'default',
                                                                minWidth: '120px',
                                                            }}
                                                        >
                                                            {mappingPriorityZone[task.ZonePriority!] ?? 'N/A'}
                                                        </Typography>
                                                    }
                                                    align="center"
                                                />
                                                {/* Task status */}
                                                <StatusPriorityDropdown
                                                    task={task}
                                                    type={DropdownType.Status}
                                                    label={task.TaskStatus}
                                                    menuItems={
                                                        task.TaskStatus !== TaskStatus.Cancelled
                                                            ? [
                                                                  TaskStatus.Planned,
                                                                  TaskStatus.Allocated,
                                                                  TaskStatus.InProgress,
                                                                  TaskStatus.Completed,
                                                                  TaskStatus.Cancelled,
                                                              ]
                                                            : [TaskStatus.Planned]
                                                    }
                                                    handleChangeStatusOrPriority={handleChangeStatusOrPriority}
                                                />
                                                {/* Task priority */}
                                                <StatusPriorityDropdown
                                                    task={task}
                                                    type={DropdownType.Priority}
                                                    label={task.TaskPriority}
                                                    menuItems={[
                                                        TaskPriority.Low,
                                                        TaskPriority.Medium,
                                                        TaskPriority.High,
                                                    ]}
                                                    handleChangeStatusOrPriority={handleChangeStatusOrPriority}
                                                />
                                                {/* Task assignee */}
                                                <CellOfRow
                                                    value={
                                                        <Tooltip
                                                            placement="top"
                                                            title={task.Assignee?.length! > 150 ? task.Assignee : ''}
                                                        >
                                                            <Typography
                                                                component={'span'}
                                                                sx={{
                                                                    ...ellipsisStyles,
                                                                    cursor: 'default',
                                                                    minWidth:
                                                                        task.Assignee?.length! < 50
                                                                            ? 'max-content'
                                                                            : !task.Assignee
                                                                            ? '120px'
                                                                            : '350px',
                                                                }}
                                                            >
                                                                {task.Assignee}
                                                            </Typography>
                                                        </Tooltip>
                                                    }
                                                    align="left"
                                                />
                                                {/* Created by */}
                                                <CellOfRow
                                                    value={<Typography component={'span'}>{task.CreatedBy}</Typography>}
                                                    align="left"
                                                />
                                                {/* Created */}
                                                <CellOfRow
                                                    value={
                                                        <Typography component={'span'}>
                                                            {formatUKDate(task.Created!)}
                                                        </Typography>
                                                    }
                                                    align="center"
                                                />
                                                {/* Complete date */}
                                                <CellOfRow
                                                    value={
                                                        <Typography component={'span'}>
                                                            {task.TaskStatus === TaskStatus.Completed
                                                                ? formatUKDate(task.CompleteDate!)
                                                                : ''}
                                                        </Typography>
                                                    }
                                                    align="center"
                                                />
                                                {/* FaultStart date */}
                                                <CellOfRow
                                                    value={
                                                        <Typography component={'span'}>
                                                            {formatUKDate(task.FaultStartDate!)}
                                                        </Typography>
                                                    }
                                                    align="center"
                                                />
                                                {/* View attachments */}
                                                <CellOfRow
                                                    value={
                                                        <Tooltip title="View attachments" placement="top">
                                                            <Badge
                                                                badgeContent={task.CameraTaskAttachments?.length}
                                                                sx={{
                                                                    '& .MuiBadge-badge': {
                                                                        right: 10,
                                                                        top: 8,
                                                                        border: `2px solid #FFFFFF`,
                                                                        padding: '0 4px',
                                                                        fontSize: 9,
                                                                        backgroundColor: color.containedButtonBg,
                                                                    },
                                                                }}
                                                            >
                                                                <IconButton
                                                                    sx={{
                                                                        backgroundColor: color.grey200,
                                                                        border: `6px solid ${color.grey100}`,
                                                                        width: '40px',
                                                                        height: '40px',
                                                                    }}
                                                                    onClick={() => {
                                                                        onSetTaskItem(task);
                                                                        popUpCountAttachments.setTrue();
                                                                    }}
                                                                >
                                                                    <IoEyeOutline
                                                                        style={{ width: '14px', height: '14px' }}
                                                                    />
                                                                </IconButton>
                                                            </Badge>
                                                        </Tooltip>
                                                    }
                                                    align="center"
                                                />
                                                {/* <CellOfRow
                                                    value={
                                                        <Tooltip
                                                            placement="top"
                                                            title={task.Problem?.length! > 150 ? task.Problem : ''}
                                                        >
                                                            <Typography
                                                                component={'span'}
                                                                sx={{
                                                                    ...ellipsisStyles,
                                                                    cursor: 'default',
                                                                    minWidth:
                                                                        task.Problem?.length! > 50
                                                                            ? '350px'
                                                                            : 'max-content',
                                                                }}
                                                            >
                                                                {task.Problem}
                                                            </Typography>
                                                        </Tooltip>
                                                    }
                                                    align="left"
                                                />
                                                <CellOfRow
                                                    value={
                                                        <Tooltip
                                                            placement="top"
                                                            title={task.Solution?.length! > 150 ? task.Solution : ''}
                                                        >
                                                            <Typography
                                                                component={'span'}
                                                                sx={{
                                                                    ...ellipsisStyles,
                                                                    cursor: 'default',
                                                                    minWidth:
                                                                        task.Solution?.length! > 50
                                                                            ? '350px'
                                                                            : 'max-content',
                                                                }}
                                                            >
                                                                {task.Solution}
                                                            </Typography>
                                                        </Tooltip>
                                                    }
                                                    align="left"
                                                /> */}
                                                {/* End date */}
                                                <CellOfRow
                                                    value={
                                                        <OverdueButton
                                                            endDate={task.EndDate}
                                                            taskStatus={task.TaskStatus}
                                                        />
                                                    }
                                                    align="center"
                                                    sx={{
                                                        position: { md: 'sticky' },
                                                        right: 168,
                                                        background: '#fff',
                                                        zIndex: 1,
                                                    }}
                                                />
                                                {/* Actions */}
                                                <CellOfRow
                                                    value={
                                                        <Stack direction={'row'} spacing={1} justifyContent={'center'}>
                                                            <Tooltip title={'View'} placement="top">
                                                                <IconButton
                                                                    sx={{
                                                                        backgroundColor: color.grey200,
                                                                        border: `6px solid ${color.grey100}`,
                                                                        width: '40px',
                                                                        height: '40px',
                                                                        position: { md: 'sticky' },
                                                                    }}
                                                                    onClick={() => {
                                                                        onSetTaskItem(task);
                                                                        popupViewCompleteTask.setTrue();
                                                                    }}
                                                                >
                                                                    <IoEyeOutline
                                                                        style={{ width: '14px', height: '14px' }}
                                                                    />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {task.TaskStatus === TaskStatus.Completed ? (
                                                                <></>
                                                            ) : (
                                                                <>
                                                                    <Tooltip title={'Edit'} placement="top">
                                                                        <IconButton
                                                                            sx={{
                                                                                backgroundColor: color.grey200,
                                                                                border: `6px solid ${color.grey100}`,
                                                                                width: '40px',
                                                                                height: '40px',
                                                                                position: 'sticky',
                                                                            }}
                                                                            onClick={() => {
                                                                                onSetTaskItem(task);
                                                                                popupUpsertTask.setTrue();
                                                                            }}
                                                                        >
                                                                            <IcEdit />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <DangerTooltip title={'Delete'} placement="top">
                                                                        <IconButton
                                                                            sx={{
                                                                                backgroundColor: color.grey200,
                                                                                border: `6px solid ${color.grey100}`,
                                                                                width: '40px',
                                                                                height: '40px',
                                                                                position: 'sticky',
                                                                            }}
                                                                            onClick={() => {
                                                                                onSetTaskItem(task);
                                                                                popupConfirmDeleteTask.setTrue();
                                                                            }}
                                                                        >
                                                                            <ICDelete fontSize={18} />
                                                                        </IconButton>
                                                                    </DangerTooltip>
                                                                </>
                                                            )}
                                                        </Stack>
                                                    }
                                                    align={'right'}
                                                    sx={{
                                                        position: { md: 'sticky' },
                                                        right: 0,
                                                        background: '#fff',
                                                        zIndex: 1,
                                                        width: '168px',
                                                    }}
                                                />
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </ScrollContainer>
                    </TableContainer>
                ) : (
                    <ListNoData />
                )}
            </Box>
        </BaseItem>
    );
}

export const SkeletonRow = () => {
    return <Skeleton variant="text" sx={{ fontSize: '14px' }} />;
};
